// * Timing for the Voice wrap-up signal. ElevenLabs hard-stops a call at the
// * agent's maximum duration and the audio cuts mid-sentence, so a contextual
// * update fires partway through the call and lets the agent end it itself.
// * This module decides only when to fire; sending the update lives elsewhere.

type ResolveRemainingWrapUpMsArgs = {
  connectedAt: number;
  delayMs: number;
  now: number;
};

type StartWrapUpTimerArgs = {
  connectedAt: number;
  delayMs: number;
  onFire: () => void;
};

/**
 * Converts the configured wrap-up moment into a delay in milliseconds.
 *
 * Returns null for a missing or broken value, so a bad configuration disables
 * the wrap-up signal instead of firing it at time zero.
 */
export const resolveWrapUpDelayMs = (
  wrapUpAtSeconds?: number | null,
): number | null => {
  if (wrapUpAtSeconds === undefined || wrapUpAtSeconds === null) {
    return null;
  }

  // * Number.isInteger also rejects NaN and Infinity, so this one guard covers
  // * every non-finite and fractional value.
  if (!Number.isInteger(wrapUpAtSeconds) || wrapUpAtSeconds <= 0) {
    return null;
  }

  return wrapUpAtSeconds * 1000;
};

/**
 * Milliseconds left until `connectedAt + delayMs`. A deadline already in the
 * past gives 0, so a timer armed late fires at once instead of never.
 */
export const resolveRemainingWrapUpMs = ({
  connectedAt,
  delayMs,
  now,
}: ResolveRemainingWrapUpMsArgs): number =>
  Math.max(0, connectedAt + delayMs - now);

/**
 * Calls `onFire` once, `delayMs` after `connectedAt`, and returns a `stop`
 * that cancels it. `stop` is safe at any time, including after the fire and
 * more than once.
 */
export const startWrapUpTimer = ({
  connectedAt,
  delayMs,
  onFire,
}: StartWrapUpTimerArgs): (() => void) => {
  const handle = setTimeout(
    onFire,
    resolveRemainingWrapUpMs({ connectedAt, delayMs, now: Date.now() }),
  );

  return () => {
    clearTimeout(handle);
  };
};
